import React, { useState, useEffect, useCallback } from 'react';
import DeliveryZoneMap from '../components/DeliveryZoneMap';
import ZoneSelector from '../components/ZoneSelector';
import { deliveryService } from '../services/deliveryService';
import { commerceService, type Commerce } from '../services/commerceService';
import { type GADMZone } from '../services/gadmService';
import { useAppSelector } from '../store/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../components/ui/notifications';
import { useDeleteConfirmation } from '../components/ui/DeleteConfirmationProvider';

// Minimal local GeoJSON-like types to avoid external dependency
type Polygon = { type: 'Polygon'; coordinates: number[][][] };
type MultiPolygon = { type: 'MultiPolygon'; coordinates: number[][][][] };
type Geometry = Polygon | MultiPolygon | any;
type Feature = { type: 'Feature'; geometry: Geometry; properties?: Record<string, any> };
type FeatureCollection = { type: 'FeatureCollection'; features: Feature[] };
interface DeliveryZone {
  id?: number;
  commerce_id: number;
  zone_name: string;
  zone_geometry: Polygon | MultiPolygon;
  is_active?: boolean;
}

interface NominatimSuggestion {
  display_name: string;
  lat: string;
  lon: string;
}

const CommerceDeliverySettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirmDelete = useDeleteConfirmation();
  const user = useAppSelector((s) => s.auth.user);
  const comercioKey = user?.comercioKey;

  const [deliveryZones, setDeliveryZones] = useState<DeliveryZone[]>([]);
  const [newZoneName, setNewZoneName] = useState<string>('');
  const [newZoneNameError, setNewZoneNameError] = useState<string | null>(null);
  const [newZoneIsActive, setNewZoneIsActive] = useState<boolean>(true);
  const [currentDrawing, setCurrentDrawing] = useState<FeatureCollection | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [commerceDetails, setCommerceDetails] = useState<Commerce | null>(null);
  const [selectedGadmZone, setSelectedGadmZone] = useState<GADMZone | null>(null);
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState<boolean>(true); // Fake toggle state

  // Address verification states (replicates OrderForm UX)
  const [direccionEntregaInput, setDireccionEntregaInput] = useState<string>('');
  const [addressSuggestions, setAddressSuggestions] = useState<NominatimSuggestion[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<{
    fullAddress: string;
    lat: number;
    lon: number;
  } | null>(null);
  const [isAddressDeliverable, setIsAddressDeliverable] = useState<boolean | null>(null);
  const [addressVerificationLoading, setAddressVerificationLoading] = useState(false);
  const [addressVerificationError, setAddressVerificationError] = useState<string | null>(null);

  const fetchCommerceDetails = useCallback(async () => {
    if (!comercioKey) return;
    setLoading(true);
    setError(null);
    try {
      const details = await commerceService.getCommerceByKey(comercioKey);
      setCommerceDetails(details);
      if (details.gadm_zone_id && details.gadm_zone_name && details.gadm_zone_geometry) {
        setSelectedGadmZone({
          id: details.gadm_zone_id,
          name: details.gadm_zone_name,
          geometry: details.gadm_zone_geometry, // Include geometry
        });
      }
    } catch (err) {
      setError(t('deliverySettingsPage.messages.fetchCommerceError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [comercioKey]);

  const fetchDeliveryZones = useCallback(async () => {
    if (!comercioKey) return; // Only fetch if comercioKey is available
    setLoading(true);
    setError(null);
    try {
      const zones = await deliveryService.getDeliveryZones(comercioKey);
      setDeliveryZones(zones);
    } catch (err) {
      setError(t('deliverySettingsPage.messages.fetchZonesError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [comercioKey]);

  useEffect(() => {
    fetchCommerceDetails();
    fetchDeliveryZones();
  }, [fetchCommerceDetails, fetchDeliveryZones]);

  // Debounce for address autocompletion (like OrderForm)
  useEffect(() => {
    const handler = setTimeout(() => {
      if (direccionEntregaInput.length > 3) {
        fetchAddressSuggestions(direccionEntregaInput);
      } else {
        setAddressSuggestions([]);
        setIsAddressDeliverable(null);
        setSelectedAddress(null);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [direccionEntregaInput]);

  // Verify when an address is selected
  useEffect(() => {
    if (selectedAddress && comercioKey) {
      verifyAddressInZone(selectedAddress.fullAddress);
    } else {
      setIsAddressDeliverable(null);
      setAddressVerificationError(null);
    }
  }, [selectedAddress, comercioKey]);

  const handleDrawComplete = (geoJSON: FeatureCollection) => {
    console.log('handleDrawComplete received GeoJSON:', geoJSON);
    setCurrentDrawing(geoJSON);
  };

  const handleSaveNewZone = async () => {
    console.log('Attempting to save new zone.');
    console.log('newZoneName:', newZoneName);
    console.log('currentDrawing:', currentDrawing);
    console.log('newZoneIsActive:', newZoneIsActive);

    // Clear previous errors
    setNewZoneNameError(null);

    if (!comercioKey) {
      setError(t('deliverySettingsPage.messages.commerceKeyMissing'));
      return;
    }

    if (!newZoneName.trim()) {
      setNewZoneNameError(t('deliverySettingsPage.messages.zoneNameEmpty'));
      return;
    }
    if (!currentDrawing || currentDrawing.features.length === 0) {
      notifications.warning('Dibuja la zona', t('deliverySettingsPage.messages.drawPolygonPrompt'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const newZone: Omit<DeliveryZone, 'id' | 'commerce_id'> = {
        zone_name: newZoneName,
        zone_geometry: currentDrawing.features[0].geometry as Polygon | MultiPolygon,
        is_active: newZoneIsActive,
      };
      await deliveryService.createDeliveryZone(comercioKey, newZone);
      setNewZoneName('');
      setNewZoneIsActive(true);
      setCurrentDrawing(null);
      fetchDeliveryZones();
      notifications.success('Zona de reparto creada', `${newZone.zone_name} se añadió correctamente.`);
    } catch (err) {
      const message = t('deliverySettingsPage.messages.saveZoneError');
      setError(message);
      notifications.error('No se pudo crear la zona', message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaultSpainZone = async () => {
    if (!comercioKey) {
      setError(t('deliverySettingsPage.messages.commerceKeyMissing'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await deliveryService.createDefaultSpainDeliveryZone(comercioKey);
      fetchDeliveryZones();
      notifications.success('Zona predeterminada creada', t('deliverySettingsPage.messages.createDefaultZoneSuccess'));
    } catch (err: any) {
      let message: string;
      if (err.response && err.response.status === 409) {
        message = t('deliverySettingsPage.messages.defaultZoneExists');
      } else {
        message = t('deliverySettingsPage.messages.addDefaultZoneError');
      }
      setError(message);
      notifications.error('No se pudo crear la zona predeterminada', message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteZone = async (zoneId: number) => {
    if (!comercioKey) {
      setError(t('deliverySettingsPage.messages.commerceKeyMissing'));
      return;
    }
    if (!await confirmDelete({
      title: t('deliverySettingsPage.buttons.delete', 'Eliminar zona'),
      message: t('deliverySettingsPage.messages.confirmDeleteZone'),
    })) return;

    setLoading(true);
    setError(null);
    try {
      await deliveryService.deleteDeliveryZone(comercioKey, zoneId);
      fetchDeliveryZones();
    } catch (err) {
      setError(t('deliverySettingsPage.messages.deleteZoneError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGadmZoneSelected = async (zone: GADMZone | null) => {
    if (!comercioKey) {
      setError(t('deliverySettingsPage.messages.commerceKeyMissing'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const updatedCommerce = await commerceService.updateCommerceGadmZone(comercioKey, zone);
      setCommerceDetails(updatedCommerce);
      setSelectedGadmZone(zone);
      alert(t('deliverySettingsPage.messages.gadmUpdatedSuccess'));
    } catch (err) {
      setError(t('deliverySettingsPage.messages.gadmUpdateError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Address suggestions via Nominatim
  const fetchAddressSuggestions = async (query: string) => {
    setAddressVerificationLoading(true);
    setAddressVerificationError(null);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=es`
      );
      const data: NominatimSuggestion[] = await response.json();
      setAddressSuggestions(data);
    } catch (err) {
      console.error('Error fetching address suggestions:', err);
      setAddressVerificationError(t('deliverySettingsPage.messages.addressSuggestionsError'));
    } finally {
      setAddressVerificationLoading(false);
    }
  };

  // Verify address against delivery zones
  const verifyAddressInZone = async (address: string) => {
    if (!comercioKey) return;
    setAddressVerificationLoading(true);
    setAddressVerificationError(null);
    try {
      const result = await deliveryService.verifyDeliveryAddress(comercioKey, address);
      setIsAddressDeliverable(result.isDeliverable);
    } catch (err) {
      console.error('Error verifying address:', err);
      setIsAddressDeliverable(false);
      setAddressVerificationError(t('deliverySettingsPage.messages.addressVerifyError'));
    } finally {
      setAddressVerificationLoading(false);
    }
  };

  const handleSelectAddress = (suggestion: NominatimSuggestion) => {
    setDireccionEntregaInput(suggestion.display_name);
    setSelectedAddress({
      fullAddress: suggestion.display_name,
      lat: parseFloat(suggestion.lat),
      lon: parseFloat(suggestion.lon),
    });
    setAddressSuggestions([]);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto dark-bg overflow-x-hidden p-4">
      <h1 className="text-3xl font-bold mb-6 text-white">{t('deliverySettingsPage.title', { key: comercioKey || t('common.na') })}</h1>

      {loading && <p>{t('deliverySettingsPage.loading')}</p>}
      {error && <p className="text-red-500">{t('deliverySettingsPage.errorPrefix')}{error}</p>}

      <div className="bg-black/30 backdrop-blur-md shadow-xl rounded-3xl overflow-hidden border border-white/20 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-2">{t('deliverySettingsPage.sections.defineZonesTitle')}</h2>
        <div className="mb-4">
          <label htmlFor="newZoneName" className="block text-sm font-medium text-white/80">{t('deliverySettingsPage.labels.zoneName')}</label>
          <input
            type="text"
            id="newZoneName"
            className={`mt-1 block w-full border ${newZoneNameError ? 'border-red-500' : 'border-white/20'} rounded-md shadow-sm p-2`}
            value={newZoneName}
            onChange={(e) => {
              setNewZoneName(e.target.value);
              if (newZoneNameError) setNewZoneNameError(null);
            }}
            placeholder={t('deliverySettingsPage.placeholders.zoneName')}
          />
          {newZoneNameError && <p className="text-red-500 text-xs mt-1">{newZoneNameError}</p>}
        </div>
        <div className="mb-4 flex items-center">
          <input
            type="checkbox"
            id="newZoneIsActive"
            className="mr-2"
            checked={newZoneIsActive}
            onChange={(e) => setNewZoneIsActive(e.target.checked)}
          />
          <label htmlFor="newZoneIsActive" className="text-sm font-medium text-white/80">{t('deliverySettingsPage.labels.isActive')}</label>
        </div>
        <DeliveryZoneMap
          onDrawComplete={handleDrawComplete}
          initialGeoJSON={{
            type: 'FeatureCollection',
            features: [
              ...(currentDrawing
                ? (currentDrawing.features as any[]).map((f: any) => ({ ...f, properties: f.properties ?? {} }))
                : []),
              ...deliveryZones.map(zone => ({
                type: 'Feature' as any,
                geometry: zone.zone_geometry as any,
                properties: { id: zone.id, name: zone.zone_name }
              })),
              ...(selectedGadmZone && (selectedGadmZone as any)?.geometry
                ? [{
                  type: 'Feature' as any,
                  geometry: (selectedGadmZone as any).geometry,
                  properties: { id: selectedGadmZone.id, name: selectedGadmZone.name, isGadmZone: true }
                }]
                : [])
            ] as any[]
          } as any}
        />
        <button
          onClick={handleSaveNewZone}
          className="btn-taratic btn-green mt-4 px-4 py-2"
        >
          {t('deliverySettingsPage.buttons.saveNewZone')}
        </button>
        <button
          onClick={handleCreateDefaultSpainZone}
          className="mt-4 ml-2 px-4 py-2 btn-taratic btn-orange"
        >
          {t('deliverySettingsPage.buttons.addDefaultSpainZone')}
        </button>
      </div>

      <div className="bg-black/30 backdrop-blur-md shadow-xl rounded-3xl overflow-hidden border border-white/20 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-2">{t('deliverySettingsPage.sections.existingZonesTitle')}</h2>
        {deliveryZones.length === 0 ? (
          <p>{t('deliverySettingsPage.messages.noZonesDefined')}</p>
        ) : (
          <ul className="border border-white/20 rounded-md divide-y divide-white/10">
            {deliveryZones.map((zone) => (
              <li key={zone.id} className="p-4 flex justify-between items-center">
                <div>
                  <p className="font-medium">{zone.zone_name}</p>
                  <p className="text-sm text-white/50">{t('deliverySettingsPage.status.status')} {zone.is_active ? t('deliverySettingsPage.status.active') : t('deliverySettingsPage.status.inactive')}</p>
                </div>
                <button
                  onClick={() => handleDeleteZone(zone.id!)}
                  className="px-3 py-1 btn-taratic btn-red !py-1 text-sm"
                >
                  {t('deliverySettingsPage.buttons.delete')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-black/30 backdrop-blur-md shadow-xl rounded-3xl overflow-hidden border border-white/20 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-2">{t('deliverySettingsPage.sections.verifyAddressTitle')}</h2>
        <div>
          <label htmlFor="direccionEntregaInput" className="block text-sm font-medium text-white/80">{t('deliverySettingsPage.labels.deliveryAddress')}</label>
          <input
            type="text"
            id="direccionEntregaInput"
            className="mt-1 block w-full border border-white/20 bg-black/20 text-white placeholder-white/50 focus:bg-black rounded-md shadow-sm p-2"
            value={direccionEntregaInput}
            onChange={(e) => {
              setDireccionEntregaInput(e.target.value);
              setSelectedAddress(null);
              setIsAddressDeliverable(null);
            }}
            placeholder={t('deliverySettingsPage.placeholders.addressSearch')}
          />
          {addressVerificationLoading && (
            <p className="text-blue-500 text-sm mt-1 flex items-center">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> {t('deliverySettingsPage.messages.verifyingAddress')}
            </p>
          )}
          {isAddressDeliverable === true && (
            <p className="text-green-600 text-sm mt-1 flex items-center">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" /> {t('deliverySettingsPage.messages.addressInside')}
            </p>
          )}
          {isAddressDeliverable === false && (
            <p className="text-red-600 text-sm mt-1 flex items-center">
              <FontAwesomeIcon icon={faTimesCircle} className="mr-2" /> {t('deliverySettingsPage.messages.addressOutside')}
            </p>
          )}
          {addressVerificationError && (
            <p className="text-red-500 text-sm mt-1">{t('deliverySettingsPage.errorPrefix')}{addressVerificationError}</p>
          )}

          {addressSuggestions.length > 0 && (
            <ul className="border border-white/20 rounded-md mt-1 max-h-48 overflow-y-auto bg-black/75 shadow-xl z-10 relative">
              {addressSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  className="p-2 cursor-pointer hover:bg-black/20"
                  onClick={() => handleSelectAddress(suggestion)}
                >
                  {suggestion.display_name}
                </li>
              ))}
            </ul>
          )}
          {selectedAddress && (
            <p className="text-white/60 text-sm mt-2">
              {t('deliverySettingsPage.labels.selectedAddress')} <span className="font-semibold">{selectedAddress.fullAddress}</span>
            </p>
          )}
        </div>
      </div>

      <div className="bg-black/30 backdrop-blur-md shadow-xl rounded-3xl overflow-hidden border border-white/20 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-2">{t('deliverySettingsPage.sections.gadmTitle')}</h2>
        <ZoneSelector
          onZoneSelected={handleGadmZoneSelected}
          initialZone={selectedGadmZone}
        />
        {commerceDetails && commerceDetails.gadm_zone_name && (
          <div className="mt-2 p-2 border border-white/20 rounded-md flex justify-between items-center">
            <p className="text-sm text-white/60">
              {t('deliverySettingsPage.labels.currentGadmZone')} <strong>{commerceDetails?.gadm_zone_name}</strong> ({commerceDetails?.gadm_zone_id})
            </p>
            <button
              onClick={() => handleGadmZoneSelected(null)}
              className="ml-4 px-2 py-1 border border-transparent text-sm font-medium rounded-md text-red-300 btn-taratic btn-red !py-1 !px-2 border border-red-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {t('deliverySettingsPage.buttons.remove')}
            </button>
          </div>
        )}
      </div>

      <div className="bg-black/30 backdrop-blur-md shadow-xl rounded-3xl overflow-hidden border border-white/20 p-6 mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-2">{t('deliverySettingsPage.sections.commerceDeliveryStatusTitle')}</h2>
          <p className="text-white/80 text-sm">
            Habilita o deshabilita las entregas para todo tu comercio. Si está desactivado, los clientes solo podrán recoger en tienda.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsDeliveryEnabled(!isDeliveryEnabled)}
          className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-black ${isDeliveryEnabled ? 'bg-green-500' : 'bg-gray-600'
            }`}
          role="switch"
          aria-checked={isDeliveryEnabled}
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isDeliveryEnabled ? 'translate-x-6' : 'translate-x-0'
              }`}
          />
        </button>
      </div>
    </div>
  );
};

export default CommerceDeliverySettingsPage;
